import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { AssetResource } from '@models/Core/Asset';
import { AssetsPipe } from '@pipes/assets.pipe';
import { FormatSecondsToMinutesPipe } from '@pipes/time/format-seconds-to-minutes.pipe';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SliderModule } from 'primeng/slider';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TooltipModule } from 'primeng/tooltip';

import { AudioProjectFormService } from '../audio-project.form';
import { AudioProjectService } from '../audio-project.service';

@Component({
  selector: 'app-audio-asset-resource',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SplitButtonModule,
    ButtonModule,
    DialogModule,
    TooltipModule,
    SliderModule,
    AssetsPipe,
    FormatSecondsToMinutesPipe,
  ],
  templateUrl: './audio-asset-resource.component.html',
  styleUrl: './audio-asset-resource.component.scss',
})
export class AudioAssetResourceComponent implements OnInit {
  @ViewChild('media') media!: ElementRef<HTMLAudioElement>;

  @Input() resource: AssetResource;

  isPlaying = false;
  isDownloading = false;
  isMiniPlayer = false;

  actions: MenuItem[] = [
    {
      icon: PrimeIcons.DOWNLOAD,
      label: 'Download',
      command: () => {
        this.download();
      },
    },
  ];

  currentTime = 0;
  duration = 0;
  volume = 100;
  isMuted = false;
  private previousVolume = 100;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly audioProjectService: AudioProjectService,
    private readonly audioProjectFormService: AudioProjectFormService,
  ) {}

  ngOnInit(): void {
    this.audioProjectFormService
      .$onActiveResourceChange(this.resource)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isPlaying) {
          this.media.nativeElement.pause();
        }
        if (this.isMiniPlayer) {
          this.isMiniPlayer = false;
        }
      });
  }

  onPlay() {
    this.audioProjectFormService.setActiveResource(this.resource);
    this.isPlaying = true;
  }

  onPause() {
    this.isPlaying = false;
  }

  onEnd() {
    this.isPlaying = false;
  }

  onTimeUpdate() {
    this.currentTime = this.media.nativeElement.currentTime;
  }

  onLoadedMetadata() {
    this.duration = this.media.nativeElement.duration;
  }

  onVolumeChange() {
    const audio = this.media.nativeElement;
    this.volume = Math.round(audio.volume * 100);
    this.isMuted = audio.muted;
  }

  onSeek(event: any) {
    this.media.nativeElement.currentTime = event.value;
  }

  onVolumeSliderChange(event: any) {
    const audio = this.media.nativeElement;
    audio.volume = event.value / 100;
    if (event.value > 0 && this.isMuted) {
      audio.muted = false;
    }
  }

  onToggleMiniPlayerClick() {
    const isAdvancedMode = !this.isMiniPlayer;
    if (isAdvancedMode) {
      this.audioProjectFormService.setActiveResource(this.resource);
    }
    this.isMiniPlayer = isAdvancedMode;
  }

  onTogglePlaybackClick() {
    if (this.isPlaying) {
      this.media.nativeElement.pause();
    } else {
      this.media.nativeElement.play();
    }
  }

  onRewindClick() {
    const audio = this.media.nativeElement;
    audio.currentTime = Math.max(0, audio.currentTime - 10);
  }

  onFastForwardClick() {
    const audio = this.media.nativeElement;
    audio.currentTime = Math.min(this.duration, audio.currentTime + 10);
  }

  onSeekBackwardClick() {
    const audio = this.media.nativeElement;
    audio.currentTime = Math.max(0, audio.currentTime - 30);
  }

  onSeekForwardClick() {
    const audio = this.media.nativeElement;
    audio.currentTime = Math.min(this.duration, audio.currentTime + 30);
  }

  onToggleMuteClick() {
    const audio = this.media.nativeElement;
    if (this.isMuted) {
      audio.muted = false;
      audio.volume = this.previousVolume / 100;
      this.isMuted = false;
    } else {
      this.previousVolume = this.volume;
      audio.muted = true;
      this.isMuted = true;
    }
  }

  onDownloadClick() {
    this.download();
  }

  private async download() {
    if (this.isDownloading) {
      return;
    }
    this.isDownloading = true;
    this.audioProjectService
      .downloadAudio({ resource: this.resource })
      .subscribe({
        next: blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.resource.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        complete: () => {
          this.isDownloading = false;
        },
      });
  }
}
